import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCouponAuth, AuthError } from '../hooks/use-coupon-auth';
import { useUserLocation } from '@/Locations/hooks/use-user-location';
import { useLocations } from '@/Locations/hooks/use-locations';
import { normalizePhoneDigits, formatPhoneDisplay, isValidPhone, toE164 } from '../lib/phone';
import { normalizeBirthdayDigits, formatBirthdayDisplay, isValidBirthday, toLLBirthdate } from '../lib/birthday';
import { IconLoader4, IconUserPlus } from '@tabler/icons-react';

interface CreateAccountSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onSignIn: () => void;
}

export const CreateAccountSheet = ({ open, onOpenChange, onSuccess, onSignIn }: CreateAccountSheetProps) => {
  const [error, setError] = useState<string | null>(null);
  const [homeStoreError, setHomeStoreError] = useState<string | null>(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    homestore: '',
    birthday: '',
    password: '',
    confirmPassword: '',
    streetaddress1: '',
    city: '',
    state: '',
    zipcode: '',
  });

  const { createAccount } = useCouponAuth();
  const { branchId } = useUserLocation();
  const { data: locations, isLoading: isLocationsLoading, isError: isLocationsError, refetch: refetchLocations } = useLocations();

  // Preselect the shopper's already-chosen store, once, when it becomes available
  useEffect(() => {
    if (branchId && !form.homestore) {
      setForm((f) => (f.homestore ? f : { ...f, homestore: branchId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const storeOptions = (locations ?? []).filter((loc) => loc.LocationCouponID);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setHomeStoreError(null);

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (!isValidPhone(form.phone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!form.homestore) {
      setHomeStoreError('Please choose your home store.');
      return;
    }

    if (form.birthday && !isValidBirthday(form.birthday)) {
      setError('Please enter a valid birthday (MM/DD/YYYY)');
      return;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!form.streetaddress1.trim() || !form.city.trim() || !form.state.trim() || !form.zipcode.trim()) {
      setError('Please enter your full mailing address');
      return;
    }

    try {
      await createAccount.mutateAsync({
        profile: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: toE164(form.phone),
          password: form.password,
          streetaddress1: form.streetaddress1.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zipcode: form.zipcode.trim(),
          homestore: form.homestore,
          ...(form.birthday ? { birthdate: toLLBirthdate(form.birthday) } : {}),
        },
      });

      onOpenChange(false);
      resetForm();
      onSuccess();
    } catch (err: unknown) {
      if (err instanceof AuthError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create account. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setForm({
      firstName: '', lastName: '', email: '', phone: '', homestore: '', birthday: '', password: '',
      confirmPassword: '', streetaddress1: '', city: '', state: '', zipcode: '',
    });
    setError(null);
    setHomeStoreError(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <IconUserPlus className="h-5 w-5" />
            Create Account
          </DialogTitle>
          <DialogDescription>
            Sign up to clip coupons and access exclusive savings.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reg-firstName">First Name</Label>
              <Input
                id="reg-firstName"
                placeholder="John"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-lastName">Last Name</Label>
              <Input
                id="reg-lastName"
                placeholder="Doe"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-email">Email</Label>
            <Input
              id="reg-email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              autoComplete="email"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-phone">Phone Number</Label>
            <div className="flex gap-2">
              <div className="flex items-center px-3 border border-input bg-background rounded-md text-muted-foreground">
                +1
              </div>
              <Input
                id="reg-phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formatPhoneDisplay(form.phone)}
                onChange={(e) => setForm({ ...form, phone: normalizePhoneDigits(e.target.value) })}
                autoComplete="tel-national"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-homestore" className="text-base">Home Store</Label>
            {isLocationsError ? (
              <div className="space-y-2">
                <p className="text-base text-destructive">We couldn't load the list of stores.</p>
                <Button type="button" variant="outline" onClick={() => refetchLocations()} className="w-full">
                  Retry
                </Button>
              </div>
            ) : (
              <Select
                value={form.homestore}
                onValueChange={(value) => {
                  setForm({ ...form, homestore: value });
                  setHomeStoreError(null);
                }}
                disabled={isLocationsLoading}
              >
                <SelectTrigger id="reg-homestore" className="h-12 text-base">
                  <SelectValue placeholder={isLocationsLoading ? 'Loading stores...' : 'Select your home store'} />
                </SelectTrigger>
                <SelectContent>
                  {storeOptions.map((loc) => (
                    <SelectItem key={loc.LocationCouponID} value={loc.LocationCouponID as string} className="text-base">
                      {loc.LocationTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {homeStoreError && <p className="text-base text-destructive">{homeStoreError}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-birthday">
              Birthday <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="reg-birthday"
              type="text"
              inputMode="numeric"
              placeholder="MM/DD/YYYY"
              value={formatBirthdayDisplay(form.birthday)}
              onChange={(e) => setForm({ ...form, birthday: normalizeBirthdayDigits(e.target.value) })}
              autoComplete="bday"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-password">Password</Label>
            <Input
              id="reg-password"
              type="password"
              placeholder="At least 6 characters"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-confirmPassword">Confirm Password</Label>
            <Input
              id="reg-confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={form.confirmPassword}
              onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
              autoComplete="new-password"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-streetaddress1">Street Address</Label>
            <Input
              id="reg-streetaddress1"
              placeholder="123 Main St"
              value={form.streetaddress1}
              onChange={(e) => setForm({ ...form, streetaddress1: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="reg-city">City</Label>
              <Input
                id="reg-city"
                placeholder="City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                required
              />
            </div>
            <div className="col-span-1 space-y-2">
              <Label htmlFor="reg-state">State</Label>
              <Input
                id="reg-state"
                placeholder="MO"
                maxLength={2}
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                required
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="reg-zipcode">ZIP Code</Label>
              <Input
                id="reg-zipcode"
                placeholder="12345"
                maxLength={5}
                value={form.zipcode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 5) {
                    setForm({ ...form, zipcode: value });
                  }
                }}
                required
              />
            </div>
          </div>

          <Button type="submit" disabled={createAccount.isPending || isLocationsLoading || isLocationsError} className="w-full">
            {createAccount.isPending ? (
              <>
                <IconLoader4 className="h-4 w-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <button
              type="button"
              onClick={() => {
                handleOpenChange(false);
                onSignIn();
              }}
              className="text-primary hover:underline"
            >
              Sign In
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
